/**
 * v3.15-1232 Ghost Daily Organizer — right-side docked command center.
 *
 * Sections: NOW / TODAY / UPCOMING with 3/5/3 caps + inline "+ More".
 * Real-time via existing WebSocket service (ghost:created / updated /
 * completed / reassigned / dismissed).  Session state (per-section
 * expand) lives in state; dock expand/collapse persists to localStorage
 * per the user directive.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../lib/api';
import wsService from '../services/websocket';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import ActivityCompletionDialog from './ActivityCompletionDialog';
import FollowUpCompleteDialog from './FollowUpCompleteDialog';  // v3.21-1612 · replaces CallOutcomeModal follow-up loop
import ClientSelector from './ClientSelector';
import GhostTaskDetailPanel from './GhostTaskDetailPanel';  // v3.18-1297 inline expand
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Check, ChevronsRight, Plus, AlertCircle, CircleDot, Star, Info,
  UserCircle2, History, Users, User, Search, ArrowRightLeft, Link as LinkIcon,
  Phone, Mail, DollarSign, Building2, Briefcase, ExternalLink, MessageSquare, Clock,
} from 'lucide-react';

// v3.16-1237 — approved Ghost trademark. NEVER swap this for a custom
// rendering; the mark is served from /public/marketing/ghost-mark.png.
const GHOST_TRADEMARK_SRC = '/marketing/ghost-mark.png';

const STORAGE_KEY           = 'ptc.gdo.expanded';
const STORAGE_KEY_SCOPE     = 'ptc.gdo.scope';
const STORAGE_KEY_SECTIONS  = 'ptc.gdo.sectionExpanded';

const PRIORITY_STYLES = {
  critical:    { dot: 'bg-red-500',    ring: 'ring-red-400',    label: 'Critical',    icon: AlertCircle,
                 border: 'border-l-red-500',    accent: 'text-red-400' },
  high:        { dot: 'bg-orange-500', ring: 'ring-orange-400', label: 'High',        icon: Star,
                 border: 'border-l-orange-500', accent: 'text-orange-400' },
  normal:      { dot: 'bg-yellow-500', ring: 'ring-yellow-400', label: 'Normal',      icon: CircleDot,
                 border: 'border-l-yellow-500', accent: 'text-yellow-400' },
  information: { dot: 'bg-blue-500',   ring: 'ring-blue-400',   label: 'Information', icon: Info,
                 border: 'border-l-blue-500',   accent: 'text-blue-400' },
};

const SECTION_META = {
  now:      { title: 'NOW',      hint: 'Immediate attention' },
  today:    { title: 'TODAY',    hint: 'Complete today' },
  overdue:  { title: 'OVERDUE',  hint: 'Missed / needs attention — never disappears' },
  upcoming: { title: 'UPCOMING', hint: 'Coming up' },
};

const MANAGER_ROLES = ['ptc_manager', 'ptc_director', 'ptc_admin', 'account_administrator'];
const isManagerRole = (role) => MANAGER_ROLES.includes((role || '').toLowerCase());

// ── time helpers ────────────────────────────────────────────────────
// v3.17-1242 (OPS-002 Slice 3) — Humanized timestamps.
// Returns { label, isOverdue, minutesUntil } for an ISO due_at.
const humanizeDue = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = Date.now();
  const diffMs = d.getTime() - now;
  const diffMin = Math.round(diffMs / 60000);
  const abs = Math.abs(diffMin);
  const isOverdue = diffMs < 0;
  let label;
  if (abs < 1) {
    label = 'Now';
  } else if (abs < 60) {
    label = isOverdue ? `${abs}m overdue` : `in ${abs}m`;
  } else if (abs < 60 * 24) {
    const hrs = Math.round(abs / 60);
    label = isOverdue ? `${hrs}h overdue` : `in ${hrs}h`;
  } else {
    const nowD = new Date();
    const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const dayDiff = Math.round((startOf(d) - startOf(nowD)) / 86400000);
    if (dayDiff === -1) label = 'Yesterday';
    else if (dayDiff === 1) label = 'Tomorrow';
    else if (dayDiff > 1 && dayDiff <= 6) label = d.toLocaleDateString(undefined, { weekday: 'short' });
    else if (dayDiff < -1 && dayDiff >= -6) label = `${Math.abs(dayDiff)}d overdue`;
    else label = d.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
  }
  return { label, isOverdue, minutesUntil: diffMin };
};

const formatDueShort = (iso) => humanizeDue(iso)?.label || null;

const nameInitials = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};


const useDockExpanded = () => {
  const [expanded, setExpanded] = useState(() => {
    if (typeof window === 'undefined') return true;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === null ? true : stored === 'true';
  });
  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, String(expanded)); } catch (_e) { /* noop */ }
  }, [expanded]);
  return [expanded, setExpanded];
};

const useIsNarrow = () => {
  const [narrow, setNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 1280 : false
  );
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 1280);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return narrow;
};


// ═══════════════════════════════════════════════════════════════════
// AssigneeChip — always-visible on-card "assigned to" indicator.
// ═══════════════════════════════════════════════════════════════════
const AssigneeChip = ({ name, self, testId }) => {
  const initials = nameInitials(name);
  const label = self ? 'Me' : (name || 'Unassigned');
  return (
    <span
      data-testid={testId}
      title={self ? 'Assigned to you' : `Assigned to ${name || 'unknown'}`}
      className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5
                  text-[10px] font-semibold max-w-[110px]
                  ${self ? 'bg-accent/15 text-accent' : 'bg-white/10 text-white/80'}`}
    >
      <span className={`inline-flex items-center justify-center h-4 w-4 rounded-full text-[9px] font-bold shrink-0
                        ${self ? 'bg-accent text-primary' : 'bg-white/20 text-white'}`}>
        {initials}
      </span>
      <span className="truncate">{label}</span>
    </span>
  );
};


// ═══════════════════════════════════════════════════════════════════
// ReassignPicker — hover-shown popover with searchable teammate list.
// ═══════════════════════════════════════════════════════════════════
const ReassignPicker = ({ item, onDone, disabled, testIdPrefix }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/users', { params: { active: true, limit: 200 } });
        const rows = Array.isArray(res?.data) ? res.data
                    : (res?.data?.users || res?.data?.items || []);
        if (!cancelled) setUsers(rows);
      } catch (_e) {
        if (!cancelled) setUsers([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = users.filter((u) => u.id && u.id !== item.assigned_to);
    const matches = !needle ? list : list.filter((u) => {
      const hay = `${u.full_name || ''} ${u.email || ''}`.toLowerCase();
      return hay.includes(needle);
    });
    return matches.slice(0, 40);
  }, [users, q, item.assigned_to]);

  // v3.17-1241 (OPS-002 #1) — group reassign teammates by company.
  const groupedFiltered = useMemo(() => {
    const groups = new Map();
    filtered.forEach((u) => {
      const key = (u.company_name || 'No Company').trim();
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(u);
    });
    return Array.from(groups.entries()).sort((a, b) => {
      if (a[0] === 'No Company') return 1;
      if (b[0] === 'No Company') return -1;
      return a[0].localeCompare(b[0]);
    });
  }, [filtered]);

  const reassign = async (u) => {
    setBusy(true);
    try {
      await api.post(`/ghost-items/${item.id}/reassign`, { assigned_to: u.id });
      toast.success(`Reassigned to ${u.full_name || u.email}`);
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reassign');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={`${testIdPrefix}-reassign-btn`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center h-6 w-6 rounded
                     text-white/70 hover:text-white hover:bg-white/10
                     opacity-0 group-hover:opacity-100 focus:opacity-100
                     disabled:opacity-30 disabled:cursor-not-allowed
                     transition"
          title="Reassign"
        >
          <ArrowRightLeft className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="left"
        align="start"
        className="w-64 p-2 bg-slate-900 border border-white/20 text-white shadow-xl"
        data-testid={`${testIdPrefix}-reassign-popover`}
      >
        <div className="text-[11px] font-bold uppercase tracking-wider text-accent mb-2 px-1">
          Reassign to
        </div>
        <div className="flex items-center gap-2 px-2 py-1.5 bg-white/5 rounded mb-1">
          <Search className="h-3.5 w-3.5 text-white/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search teammates…"
            className="bg-transparent outline-none text-xs text-white placeholder:text-white/40 flex-1"
            data-testid={`${testIdPrefix}-reassign-search`}
            autoFocus
          />
        </div>
        <div className="max-h-56 overflow-y-auto">
          {groupedFiltered.length === 0 && (
            <div className="text-xs text-white/50 px-2 py-4 text-center">
              No matches
            </div>
          )}
          {groupedFiltered.map(([company, list]) => (
            <div key={company}>
              <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-accent bg-white/[0.04] sticky top-0">
                {company}
              </div>
              {list.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  disabled={busy}
                  onClick={() => reassign(u)}
                  data-testid={`${testIdPrefix}-reassign-option-${u.id}`}
                  className="w-full text-left flex items-center gap-2 px-2 py-1.5
                             rounded hover:bg-white/10 disabled:opacity-40 transition"
                >
                  <span className="inline-flex items-center justify-center h-6 w-6
                                   rounded-full bg-white/20 text-[10px] font-bold text-white shrink-0">
                    {nameInitials(u.full_name || u.email)}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs font-semibold text-white truncate">
                      {u.full_name || u.email}
                    </span>
                    {u.role && (
                      <span className="block text-[10px] text-white/50 truncate">{u.role}</span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};


// ═══════════════════════════════════════════════════════════════════
// DetailsPopover — audit trail viewer.
// ═══════════════════════════════════════════════════════════════════
const AUDIT_ACTION_LABELS = {
  created:    { label: 'Created',    tone: 'bg-blue-500/20 text-blue-300' },
  assigned:   { label: 'Assigned',   tone: 'bg-cyan-500/20 text-cyan-300' },
  reassigned: { label: 'Reassigned', tone: 'bg-amber-500/20 text-amber-300' },
  completed:  { label: 'Completed',  tone: 'bg-emerald-500/20 text-emerald-300' },
};

const DetailsPopover = ({ item, testIdPrefix }) => {
  const [open, setOpen] = useState(false);
  const [trail, setTrail] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/ghost-items/${item.id}/audit`);
        if (!cancelled) setTrail(res?.data?.audit_trail || []);
      } catch (_e) {
        if (!cancelled) setTrail([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, item.id]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testIdPrefix}-details-btn`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center h-6 w-6 rounded
                     text-white/70 hover:text-white hover:bg-white/10
                     opacity-0 group-hover:opacity-100 focus:opacity-100
                     transition"
          title="View history"
        >
          <History className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="left"
        align="start"
        className="w-72 p-3 bg-slate-900 border border-white/20 text-white shadow-xl"
        data-testid={`${testIdPrefix}-details-popover`}
      >
        <div className="text-[11px] font-bold uppercase tracking-wider text-accent mb-2">
          Item History
        </div>
        {loading ? (
          <div className="text-xs text-white/50 py-3 text-center">Loading…</div>
        ) : trail.length === 0 ? (
          <div className="text-xs text-white/50 py-3 text-center">No history recorded.</div>
        ) : (
          <div className="max-h-64 overflow-y-auto space-y-2">
            {trail.map((e) => {
              const meta = AUDIT_ACTION_LABELS[e.action] || { label: e.action, tone: 'bg-white/10 text-white/70' };
              return (
                <div key={e.id || `${e.action}-${e.at}`} className="rounded border border-white/10 bg-white/[0.03] p-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`inline-block text-[10px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5 ${meta.tone}`}>
                      {meta.label}
                    </span>
                    <span className="text-[10px] text-white/40">
                      {e.at ? new Date(e.at).toLocaleString(undefined, {
                        month: 'numeric', day: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      }) : ''}
                    </span>
                  </div>
                  <div className="text-xs text-white/80 mt-1">
                    {e.by_user_name || 'System'}
                    {e.action === 'completed' && e.outcome && (
                      <span className="text-white/50">
                        {' '}· outcome: <span className="text-white">{e.outcome}</span>
                      </span>
                    )}
                  </div>
                  {e.notes && (
                    <div className="text-[11px] text-white/60 mt-1 italic line-clamp-3">
                      &ldquo;{e.notes}&rdquo;
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};


// ═══════════════════════════════════════════════════════════════════
// Item card — v3.16-1239 dense redesign.
//
// Layout (width ≈ 224px):
//   Row 1: [priority dot + label]           [due timestamp]
//   Row 2: bold title (line-clamp-2)
//   Row 3: optional muted description
//   Row 4: [Complete pill] [assignee chip]  [hover: History + Reassign]
// ═══════════════════════════════════════════════════════════════════
const GhostItemCard = ({ item, onComplete, completingId, isMine, currentUser, onReassigned, showAssignee, onSelect, isSelected }) => {
  const style = PRIORITY_STYLES[item.priority] || PRIORITY_STYLES.normal;
  const isCompleting = completingId === item.id;
  // v3.17-1242 — humanized time + overdue signals.
  const due = useMemo(() => humanizeDue(item.due_at), [item.due_at]);
  const isOverdue = !!due?.isOverdue;
  const testIdPrefix = `ghost-item-${item.id}`;
  const canReassign = isManagerRole(currentUser?.role);
  const assigneeName = item.assigned_to_name || null;
  const self = item.assigned_to === currentUser?.id;
  // v3.18-1323 — team routing chip: this item was routed to a Team.
  const assignedTeamName = item.assigned_team_name || null;
  const assignedTeamType = item.assigned_team_type || null;
  const navigate = useNavigate();
  const location = useLocation();
  const clientId = item.client_id;
  const clientName = item.client_name;
  const clientCompany = item.client_company;
  // v3.18-1351 GHOST-CONTEXT — every operator-facing identifier the
  // rep needs to answer "which client, which deal, which rep?"
  // WITHOUT opening the client page.
  const clientPhone       = item.client_phone;
  const clientEmail       = item.client_email;
  const clientStage       = item.client_stage;
  const clientDealAmount  = item.client_deal_amount;
  const clientFundingCo   = item.client_funding_company;
  const clientRepName     = item.client_assigned_rep_name;
  // v3.19-1431 GHOST-NAV — Open Deal action + Source label.
  const dealId            = item.deal_id;
  const sourceLabel       = item.source_label || null;
  const taskType          = (item.title || '').trim();
  // v3.20-1500 GHOST-CONTEXT — surface every identifier the operator
  // needs to answer "which client / deal / next appointment" without
  // leaving Ghost.
  const applicationId     = item.application_id || item.client_application_id || null;
  const nextAppointment   = item.next_appointment_at || item.next_appointment || item.client_next_appointment || null;
  const dealAmountFmt     = (typeof clientDealAmount === 'number' && clientDealAmount > 0)
    ? clientDealAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    : null;

  // v3.20-1506 GHOST-CONTEXT — surface the appointment id + scheduled
  // start time so the card renders an "Appointment" action button
  // that opens the appointment record and a canonical Appointment
  // Date/Time chip alongside APP / DEAL / Phone.
  const appointmentId     = item.appointment_id || (item.metadata && item.metadata.appointment_id) || null;
  const appointmentAt     = item.appointment_at || (item.metadata && item.metadata.appointment_at) || null;
  const appointmentAtFmt  = appointmentAt
    ? new Date(appointmentAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : null;

  const openClient = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!clientId) {
      // v3.18-1280 DEFECT-2 fix — never silently swallow. Toast + log.
      console.warn('[GhostDailyOrganizer] Task has no associated client_id', { taskId: item.id, title: item.title });
      toast.error('This Ghost task has no associated client. Please contact support if this persists.');
      return;
    }
    // v3.21-1620 · Ghost Daily Organizer row-click now opens the
    // STANDARD Client Workspace instead of navigating to a separate
    // detail page.  This is the "click client → work the client from
    // ONE dependable place" flow demanded by v3.21-1620.  We dispatch
    // the global event so this works even if the provider hook is not
    // reachable via context (e.g. rendered outside DashboardLayout).
    try {
      window.dispatchEvent(new CustomEvent('client-workspace:open', {
        detail: { clientId },
      }));
    } catch (err) {
      console.warn('[GhostDailyOrganizer] Failed to open Client Workspace', err);
    }
  };

  // v3.19-1431 · Open Deal navigates to the client detail page with a
  // `deal` query param.  A dedicated /deals/{id} route is on the
  // roadmap; today the client page focuses the deal section.
  const openDeal = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!clientId || !dealId) {
      toast.info('No deal linked to this task.');
      return;
    }
    navigate(`/clients/${clientId}?deal=${dealId}`, {
      state: { returnTo: location.pathname + (location.search || ''), returnLabel: 'Ghost Daily Organizer', focusDealId: dealId },
    });
  };

  const stop = (e) => { if (e && e.stopPropagation) e.stopPropagation(); };
  const callClient = (e) => {
    stop(e);
    if (!clientPhone) return toast.info('No phone number on file for this client.');
    window.location.href = `tel:${clientPhone}`;
  };
  const smsClient = (e) => {
    stop(e);
    if (!clientPhone) return toast.info('No phone number on file for this client.');
    window.location.href = `sms:${clientPhone}`;
  };
  const emailClient = (e) => {
    stop(e);
    if (!clientEmail) return toast.info('No email on file for this client.');
    window.location.href = `mailto:${clientEmail}`;
  };

  // v3.20-1506 · Appointment action — opens the appointment record.
  const openAppointment = (e) => {
    stop(e);
    if (!appointmentId) return toast.info('No appointment linked to this task.');
    navigate(`/appointments/${appointmentId}`, {
      state: { returnTo: location.pathname + (location.search || ''), returnLabel: 'Ghost Daily Organizer' },
    });
  };

  const handleRowClick = (e) => {
    const hit = e.target.closest('button, a, [role="button"], [data-no-row-click]');
    if (hit && hit !== e.currentTarget) return;
    if (typeof onSelect === 'function') {
      onSelect(item);
    } else {
      openClient(e);
    }
  };
  const handleRowKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (typeof onSelect === 'function') onSelect(item);
      else openClient(e);
    }
  };

  return (
    <div
      data-testid={testIdPrefix}
      role={clientId ? 'button' : undefined}
      tabIndex={clientId ? 0 : undefined}
      onClick={handleRowClick}
      onKeyDown={clientId ? handleRowKeyDown : undefined}
      aria-label={clientId ? `Open client ${clientName || ''}` : undefined}
      className={`group relative rounded-md border-l-4 ${style.border}
                  ${isOverdue ? 'bg-red-500/[0.08] hover:bg-red-500/[0.14] ring-1 ring-red-500/40 animate-pulse-slow'
                              : 'bg-white/[0.05] hover:bg-white/[0.08]'}
                  ${isSelected ? 'ring-2 ring-accent shadow-lg' : ''}
                  ${clientId ? 'cursor-pointer' : ''}
                  px-2.5 py-2
                  transition-all duration-500
                  ${isCompleting ? 'opacity-0 scale-95' : 'opacity-100'}`}
    >
      {/* Row 1 — priority + OVERDUE badge + due */}
      <div className="flex items-center gap-1.5 mb-1">
        <span className={`h-2 w-2 rounded-full ${style.dot} shrink-0`} />
        <span className={`text-[11px] font-bold uppercase tracking-wide ${style.accent}`}>
          {style.label}
        </span>
        {isOverdue && (
          <span
            data-testid={`${testIdPrefix}-overdue-badge`}
            className="text-[9px] font-black uppercase tracking-widest text-white bg-red-600 rounded px-1 py-[1px]"
          >
            Overdue
          </span>
        )}
        {due?.label && (
          <span
            data-testid={`${testIdPrefix}-due`}
            className={`ml-auto text-[11px] font-semibold ${isOverdue ? 'text-red-400' : 'text-white/70'}`}
          >
            {due.label}
          </span>
        )}
      </div>

      {/* v3.19-1431 GHOST-NAV — Task title FIRST, client identity
          directly beneath (per product spec).  This is the reverse
          of v3.18-1351: operators asked for the task type to lead
          again so they know WHAT action is required before scanning
          WHO it's for. */}
      {taskType && (
        <div
          data-testid={`${testIdPrefix}-task-title`}
          className="text-[14px] font-bold text-white leading-tight line-clamp-2 mb-0.5"
        >
          {taskType}
        </div>
      )}
      {clientId && clientName ? (
        <div data-testid={`${testIdPrefix}-client-block`} className="mb-1">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <div
                data-testid={`${testIdPrefix}-client-name`}
                className="text-[13px] font-semibold text-accent leading-tight truncate"
              >
                {clientName}
              </div>
              <div className="text-[11px] text-white/70 leading-snug truncate flex flex-wrap gap-x-2">
                {clientCompany && (
                  <span className="inline-flex items-center gap-0.5" data-testid={`${testIdPrefix}-client-company`}>
                    <Building2 className="h-2.5 w-2.5 opacity-70" /> {clientCompany}
                  </span>
                )}
                {clientRepName && (
                  <span className="inline-flex items-center gap-0.5" data-testid={`${testIdPrefix}-client-rep`}>
                    <User className="h-2.5 w-2.5 opacity-70" /> {clientRepName}
                  </span>
                )}
                {clientStage && (
                  <span className="inline-flex items-center gap-0.5" data-testid={`${testIdPrefix}-client-stage`}>
                    <Briefcase className="h-2.5 w-2.5 opacity-70" /> {clientStage}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={openClient}
              data-testid={`${testIdPrefix}-open-client`}
              title="Open client record"
              className="shrink-0 inline-flex items-center gap-1 rounded-md
                         bg-accent/15 hover:bg-accent/30 border border-accent/40
                         px-1.5 py-1 text-[10px] font-semibold text-accent
                         transition"
            >
              <ExternalLink className="h-3 w-3" />
              Open
            </button>
          </div>
          {/* Contact + financial chips — always show what we have. */}
          {(clientPhone || clientEmail || dealAmountFmt || clientFundingCo || applicationId || dealId || nextAppointment || appointmentAtFmt) && (
            <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] text-white/70">
              {applicationId && (
                <span className="inline-flex items-center gap-0.5 font-mono text-white/80" data-testid={`${testIdPrefix}-client-app-id`}>
                  APP {applicationId}
                </span>
              )}
              {dealId && (
                <span className="inline-flex items-center gap-0.5 font-mono text-amber-300" data-testid={`${testIdPrefix}-client-deal-id`}>
                  DEAL {String(dealId).slice(0, 8)}
                </span>
              )}
              {appointmentAtFmt && (
                <span className="inline-flex items-center gap-0.5 text-emerald-300" data-testid={`${testIdPrefix}-appt-at`}>
                  <Clock className="h-2.5 w-2.5 opacity-70" /> Appt {appointmentAtFmt}
                </span>
              )}
              {nextAppointment && !appointmentAtFmt && (
                <span className="inline-flex items-center gap-0.5 text-emerald-300" data-testid={`${testIdPrefix}-client-next-appt`}>
                  <Clock className="h-2.5 w-2.5 opacity-70" /> Next {new Date(nextAppointment).toLocaleString([], {month:'short',day:'numeric',hour:'numeric',minute:'2-digit'})}
                </span>
              )}
              {clientPhone && (
                <span className="inline-flex items-center gap-0.5" data-testid={`${testIdPrefix}-client-phone`}>
                  <Phone className="h-2.5 w-2.5 opacity-70" /> {clientPhone}
                </span>
              )}
              {clientEmail && (
                <span className="inline-flex items-center gap-0.5 truncate max-w-[240px]" data-testid={`${testIdPrefix}-client-email`}>
                  <Mail className="h-2.5 w-2.5 opacity-70" /> {clientEmail}
                </span>
              )}
              {dealAmountFmt && (
                <span className="inline-flex items-center gap-0.5 font-semibold text-emerald-300" data-testid={`${testIdPrefix}-client-deal-amount`}>
                  <DollarSign className="h-2.5 w-2.5 opacity-70" /> {dealAmountFmt}
                </span>
              )}
              {clientFundingCo && (
                <span className="inline-flex items-center gap-0.5" data-testid={`${testIdPrefix}-client-funding`}>
                  <Building2 className="h-2.5 w-2.5 opacity-70" /> {clientFundingCo}
                </span>
              )}
            </div>
          )}
          {/* v3.19-1431 · Quick action row — Call / SMS / Email /
              Open Deal — all client-scoped one-click. */}
          <div className="mt-1.5 flex flex-wrap gap-1">
            <button
              type="button"
              onClick={callClient}
              disabled={!clientPhone}
              data-no-row-click
              data-testid={`${testIdPrefix}-call-btn`}
              title={clientPhone ? `Call ${clientPhone}` : 'No phone on file'}
              className="inline-flex items-center gap-1 rounded-full bg-emerald-500/20 hover:bg-emerald-500/40 disabled:opacity-40 disabled:cursor-not-allowed border border-emerald-400/40 px-2 py-0.5 text-[10px] font-semibold text-emerald-300 transition"
            >
              <Phone className="h-2.5 w-2.5" /> Call
            </button>
            <button
              type="button"
              onClick={smsClient}
              disabled={!clientPhone}
              data-no-row-click
              data-testid={`${testIdPrefix}-sms-btn`}
              title={clientPhone ? `Text ${clientPhone}` : 'No phone on file'}
              className="inline-flex items-center gap-1 rounded-full bg-sky-500/20 hover:bg-sky-500/40 disabled:opacity-40 disabled:cursor-not-allowed border border-sky-400/40 px-2 py-0.5 text-[10px] font-semibold text-sky-300 transition"
            >
              <MessageSquare className="h-2.5 w-2.5" /> SMS
            </button>
            <button
              type="button"
              onClick={emailClient}
              disabled={!clientEmail}
              data-no-row-click
              data-testid={`${testIdPrefix}-email-btn`}
              title={clientEmail ? `Email ${clientEmail}` : 'No email on file'}
              className="inline-flex items-center gap-1 rounded-full bg-violet-500/20 hover:bg-violet-500/40 disabled:opacity-40 disabled:cursor-not-allowed border border-violet-400/40 px-2 py-0.5 text-[10px] font-semibold text-violet-300 transition"
            >
              <Mail className="h-2.5 w-2.5" /> Email
            </button>
            {dealId && (
              <button
                type="button"
                onClick={openDeal}
                data-no-row-click
                data-testid={`${testIdPrefix}-open-deal`}
                title="Open the related deal"
                className="inline-flex items-center gap-1 rounded-full bg-amber-500/20 hover:bg-amber-500/40 border border-amber-400/40 px-2 py-0.5 text-[10px] font-semibold text-amber-300 transition"
              >
                <Briefcase className="h-2.5 w-2.5" /> Open Deal
              </button>
            )}
            {appointmentId && (
              <button
                type="button"
                onClick={openAppointment}
                data-no-row-click
                data-testid={`${testIdPrefix}-appt-btn`}
                title="Open the appointment record"
                className="inline-flex items-center gap-1 rounded-full bg-teal-500/20 hover:bg-teal-500/40 border border-teal-400/40 px-2 py-0.5 text-[10px] font-semibold text-teal-300 transition"
              >
                <Clock className="h-2.5 w-2.5" /> Appointment
              </button>
            )}
          </div>
          {/* v3.19-1431 · Source label — tells the operator WHERE
              this task came from (Appointment, Follow-up, Deal
              Stage, etc.). */}
          {sourceLabel && sourceLabel !== 'Manual' && (
            <div
              data-testid={`${testIdPrefix}-source-label`}
              className="mt-1 text-[10px] uppercase tracking-wider font-semibold text-white/50"
            >
              Source: <span className="text-white/70">{sourceLabel}</span>
            </div>
          )}
        </div>
      ) : (
        // No client attached — task title already shown above; also
        // surface the source so the operator knows the origin.
        sourceLabel && sourceLabel !== 'Manual' && (
          <div
            data-testid={`${testIdPrefix}-source-label`}
            className="text-[10px] uppercase tracking-wider font-semibold text-white/50 mb-0.5"
          >
            Source: <span className="text-white/70">{sourceLabel}</span>
          </div>
        )
      )}

      {/* Optional description */}
      {item.description && (
        <div className="text-[11px] text-white/55 line-clamp-1 leading-snug">
          {item.description}
        </div>
      )}

      {/* Row 4 — actions + assignee */}
      <div className="flex items-center gap-1.5 mt-1.5">
        <button
          type="button"
          disabled={!isMine && !canReassign}
          onClick={() => onComplete(item)}
          data-testid={`ghost-item-complete-${item.id}`}
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5
                      text-[11px] font-semibold transition shrink-0
                      ${isCompleting
                        ? 'bg-emerald-500 text-white'
                        : (isMine || canReassign)
                          ? 'bg-white/10 text-white hover:bg-emerald-500 hover:text-white'
                          : 'bg-white/5 text-white/40 cursor-not-allowed'}`}
          title={isMine ? 'Mark complete' : canReassign ? 'Manager: complete on assignee\'s behalf' : 'Assigned to another user'}
        >
          <span className={`inline-flex items-center justify-center h-3.5 w-3.5
                            rounded-full border ${isCompleting ? 'bg-white/20 border-white' : 'border-current'}`}>
            {isCompleting && <Check className="h-2.5 w-2.5" strokeWidth={3} />}
          </span>
          Complete
        </button>

        {showAssignee && assigneeName && (
          <AssigneeChip
            name={assigneeName}
            self={self}
            testId={`${testIdPrefix}-assignee`}
          />
        )}

        {/* v3.18-1323 — Team routing chip */}
        {assignedTeamName && (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium border ${
              assignedTeamType === 'vo' ? 'bg-indigo-50 text-indigo-800 border-indigo-200'
            : assignedTeamType === 'sales' ? 'bg-blue-50 text-blue-800 border-blue-200'
            : assignedTeamType === 'concierge' ? 'bg-teal-50 text-teal-800 border-teal-200'
            : assignedTeamType === 'funding' ? 'bg-amber-50 text-amber-800 border-amber-200'
            : assignedTeamType === 'applications' ? 'bg-violet-50 text-violet-800 border-violet-200'
            :                             'bg-slate-100 text-slate-700 border-slate-200'
            }`}
            title={`Routed to team: ${assignedTeamName}`}
            data-testid={`${testIdPrefix}-team`}
          >
            Team · {assignedTeamName}
          </span>
        )}

        <div className="ml-auto flex items-center gap-0.5 shrink-0">
          <DetailsPopover item={item} testIdPrefix={testIdPrefix} />
          {canReassign && (
            <ReassignPicker
              item={item}
              onDone={onReassigned}
              disabled={isCompleting}
              testIdPrefix={testIdPrefix}
            />
          )}
        </div>
      </div>

      {isCompleting && (
        <div className="text-[11px] text-emerald-400 font-semibold mt-1">
          Completed
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// Section
// ═══════════════════════════════════════════════════════════════════
const GhostSection = ({
  name, section, onComplete, completingId, expanded, onToggleExpand, userId,
  currentUser, onReassigned, showAssignee, onSelect, selectedId,
}) => {
  const meta = SECTION_META[name];
  const visible = section?.visible || [];
  const overflow = section?.overflow || [];
  const overflowCount = section?.overflow_count || 0;

  const shown = expanded ? [...visible, ...overflow] : visible;

  return (
    <div className="mb-5" data-testid={`ghost-section-${name}`}>
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="h-[3px] w-4 rounded-full bg-accent shrink-0" />
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-accent">
            {meta.title}
          </span>
          <span className="text-[11px] text-white/50 truncate">{meta.hint}</span>
        </div>
        <span className="text-xs font-semibold text-white/60 shrink-0 bg-white/10 rounded-full px-2 py-0.5">
          {section?.total || 0}
        </span>
      </div>

      {shown.length === 0 ? (
        <div className="text-xs italic text-white/40 px-1 py-1">
          Nothing here.
        </div>
      ) : (
        <div className="space-y-2">
          {shown.map((it) => (
            <GhostItemCard
              key={it.id}
              item={it}
              onComplete={onComplete}
              completingId={completingId}
              isMine={it.assigned_to === userId}
              currentUser={currentUser}
              onReassigned={onReassigned}
              showAssignee={showAssignee}
              onSelect={onSelect}
              isSelected={selectedId === it.id}
            />
          ))}
        </div>
      )}

      {overflowCount > 0 && (
        <button
          type="button"
          onClick={() => onToggleExpand(name)}
          data-testid={`ghost-section-more-${name}`}
          className="w-full mt-1.5 text-xs font-semibold text-accent
                     hover:text-gold-light py-1 rounded hover:bg-white/5 transition"
        >
          {expanded ? `Show less` : `+ ${overflowCount} more`}
        </button>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════════════════════════════
// Create-item dialog (all roles can create for themselves; managers+ for others)
// ═══════════════════════════════════════════════════════════════════
const CreateGhostItemDialog = ({ open, onOpenChange, currentUser, onCreated, defaultClient = null }) => {
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    title: '', description: '', priority: 'normal', due_at: '',
    assigned_to: currentUser?.id || '',
    require_completion_details: false,
    client: defaultClient,   // { id, full_name, company_name } | null
  });

  useEffect(() => {
    if (open) {
      setForm({ title: '', description: '', priority: 'normal', due_at: '',
                assigned_to: currentUser?.id || '',
                require_completion_details: false,
                client: defaultClient });
    }
  }, [open, currentUser?.id, defaultClient]);

  // Load assignable users so managers+ can send Ghost cards person-to-person.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/users', { params: { active: true, limit: 500 } });
        const list = Array.isArray(res?.data) ? res.data
                     : (res?.data?.users || res?.data?.items || []);
        if (!cancelled) setUsers(list);
      } catch (_e) {
        // Non-fatal — dialog still works for self-assignment.
        if (!cancelled) setUsers([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const role = (currentUser?.role || '').toLowerCase();
  const canAssignOthers = ['ptc_manager', 'ptc_director', 'ptc_admin'].includes(role);

  const submit = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      const res = await api.post('/ghost-items', {
        title: form.title.trim(),
        description: form.description || null,
        assigned_to: form.assigned_to || currentUser?.id,
        priority: form.priority,
        source: 'manual_priority',
        due_at: form.due_at ? new Date(form.due_at).toISOString() : null,
        require_completion_details: !!form.require_completion_details,
        client_id: form.client?.id || null,
      });
      toast.success('Ghost item created');
      onCreated?.(res?.data?.item);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create item');
    } finally {
      setSaving(false);
    }
  };

  const assigneeOptions = useMemo(() => {
    const base = users.length > 0 ? users : [{ id: currentUser?.id,
                                               full_name: currentUser?.full_name || 'Me',
                                               email: currentUser?.email,
                                               company_name: currentUser?.company_name }];
    const withSelf = base.some((u) => u.id === currentUser?.id) ? base
                     : [{ id: currentUser?.id,
                          full_name: currentUser?.full_name || 'Me',
                          email: currentUser?.email,
                          company_name: currentUser?.company_name }, ...base];
    return withSelf.filter((u) => u.id);
  }, [users, currentUser]);

  // v3.17-1241 (OPS-002 #1) — Group Assign To by company for the dropdown.
  const groupedAssignees = useMemo(() => {
    const groups = new Map();
    assigneeOptions.forEach((u) => {
      const key = (u.company_name || 'No Company').trim();
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(u);
    });
    // Sort companies alphabetically; put "No Company" last.
    const entries = Array.from(groups.entries()).sort((a, b) => {
      if (a[0] === 'No Company') return 1;
      if (b[0] === 'No Company') return -1;
      return a[0].localeCompare(b[0]);
    });
    // Within each group, sort by name.
    entries.forEach(([, list]) => list.sort((a, b) =>
      String(a.full_name || a.email || '').localeCompare(String(b.full_name || b.email || ''))
    ));
    return entries;
  }, [assigneeOptions]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="ghost-create-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Add Ghost Item</DialogTitle>
          <DialogDescription>
            Create a task or reminder for yourself. Managers may also assign to teammates.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Call Ms. Bowley"
              data-testid="ghost-create-title"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Optional context"
              rows={2}
              data-testid="ghost-create-desc"
            />
          </div>
          {/* v3.17-1241 (OPS-002 #2) — Related Client (optional). */}
          <div>
            <Label className="text-xs">Related Client</Label>
            <ClientSelector
              value={form.client}
              onChange={(c) => setForm({ ...form, client: c })}
              placeholder="Link a client (optional)…"
              testIdPrefix="ghost-create-client"
            />
          </div>
          {/* Person-to-person assignment. Employees see themselves only;
              managers/directors/admins see the full team, grouped by company. */}
          <div>
            <Label className="text-xs">Assign to *</Label>
            <Select
              value={form.assigned_to || currentUser?.id}
              onValueChange={(v) => setForm({ ...form, assigned_to: v })}
              disabled={!canAssignOthers}
            >
              <SelectTrigger data-testid="ghost-create-assignee">
                <SelectValue placeholder="Select assignee" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {groupedAssignees.map(([company, list]) => (
                  <div key={company} data-testid={`ghost-create-assignee-group-${company.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                    <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-500 bg-slate-50 sticky top-0">
                      {company}
                    </div>
                    {list.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.full_name || u.email || u.id}
                        {u.id === currentUser?.id ? ' (me)' : ''}
                      </SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
            {!canAssignOthers && (
              <p className="text-[10px] text-slate-400 mt-1">
                Employees can only assign items to themselves. Managers and above may reassign.
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Priority</Label>
              <Select
                value={form.priority}
                onValueChange={(v) => setForm({ ...form, priority: v })}
              >
                <SelectTrigger data-testid="ghost-create-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critical (Red)</SelectItem>
                  <SelectItem value="high">High (Orange)</SelectItem>
                  <SelectItem value="normal">Normal (Yellow)</SelectItem>
                  <SelectItem value="information">Information (Blue)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Due at</Label>
              <Input
                type="datetime-local"
                value={form.due_at}
                onChange={(e) => setForm({ ...form, due_at: e.target.value })}
                data-testid="ghost-create-due"
              />
            </div>
          </div>
          {/* v3.16-1238 — creator can force outcome + notes on completion. */}
          <label className="flex items-start gap-2 pt-1 cursor-pointer">
            <Checkbox
              checked={!!form.require_completion_details}
              onCheckedChange={(v) => setForm({ ...form, require_completion_details: !!v })}
              data-testid="ghost-create-require-completion"
              className="mt-0.5"
            />
            <span className="text-xs text-slate-700 leading-tight">
              <span className="font-semibold">Require completion details</span>
              <br />
              <span className="text-slate-500">
                Assignee must record an outcome and notes before closing this item.
              </span>
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="ghost-create-submit"
                  className="bg-accent hover:bg-gold-dark text-white">
            {saving ? 'Saving…' : 'Add Ghost Item'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ═══════════════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════════════
export const GhostDailyOrganizer = ({ currentUser }) => {
  const location = useLocation();
  const navigate = useNavigate();   // v3.18-1351 GHOST-CONTEXT — for the Completion Dialog "Open Client Record" jump
  const [expanded, setExpanded] = useDockExpanded();
  const narrow = useIsNarrow();
  const effectiveExpanded = narrow ? false : expanded;

  const [sections, setSections] = useState({
    now:      { visible: [], overflow: [], overflow_count: 0, total: 0 },
    today:    { visible: [], overflow: [], overflow_count: 0, total: 0 },
    upcoming: { visible: [], overflow: [], overflow_count: 0, total: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [sectionExpanded, setSectionExpanded] = useState(() => {
    // v3.18-1285 — Persist per-section expand/collapse so returning
    // from a client page (which unmounts child pages) restores the
    // operator's exact prior state.
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY_SECTIONS);
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          now:      Boolean(parsed?.now),
          today:    Boolean(parsed?.today),
          overdue:  parsed?.overdue !== false,   // v3.21-1823 · default OPEN
          upcoming: Boolean(parsed?.upcoming),
        };
      }
    } catch (_e) { /* noop */ }
    return { now: false, today: false, overdue: true, upcoming: false };
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY_SECTIONS, JSON.stringify(sectionExpanded));
    } catch (_e) { /* noop */ }
  }, [sectionExpanded]);
  const [createOpen, setCreateOpen] = useState(false);
  const [completingId, setCompletingId] = useState(null);
  const completingTimer = useRef(null);
  // v3.16-1239 — Mine/Team scope. Managers+ may toggle; employees are locked to Mine.
  // v3.18-1285 — Persisted to localStorage so the operator's scope
  // choice survives route changes and page reloads.
  const canSeeTeam = isManagerRole(currentUser?.role);
  const [scope, setScope] = useState(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY_SCOPE);
      if (raw === 'me' || raw === 'team') return raw;
    } catch (_e) { /* noop */ }
    return 'me';
  });
  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY_SCOPE, scope); } catch (_e) { /* noop */ }
  }, [scope]);

  const fetchItems = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const params = { scope: canSeeTeam ? scope : 'me' };
      const res = await api.get('/ghost-items', { params });
      setSections(res?.data?.sections || {});
    } catch (err) {
      // silent fail — the dock never blocks the UI
      console.warn('[GDO] load failed', err?.response?.data || err);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id, scope, canSeeTeam]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // v3.21-1789 · Debounced fetch — coalesces bursts of invalidation
  // events (task assign + reschedule + reassign in a workflow) into
  // a single unified feed refresh instead of a request storm.
  const debounceTimerRef = useRef(null);
  const scheduleFetch = useCallback((_reason) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      fetchItems();
    }, 250);
  }, [fetchItems]);

  // v3.21-1789 · SERVER-SIDE Ghost invalidation.  Every canonical
  // task/appointment write funnels through
  // `services/ghost_projection.py::notify_ghost_work_changed`, which
  // fires a `ghost_work_changed` broadcast over the existing
  // `ws_manager`.  Subscribing here means GhostDailyOrganizer
  // refetches its unified feed WITHOUT depending on which frontend
  // component wrote the row — public booking, background workers,
  // and cross-session mutations all invalidate the same way.
  useEffect(() => {
    const unsub = wsService.subscribe?.('ghost_work_changed', (data) => {
      scheduleFetch(data?.operation || 'server_event');
    });
    return () => { try { unsub && unsub(); } catch (_e) { /* noop */ } };
  }, [scheduleFetch]);

  // v3.21-1788 · Live cross-component refresh.  Any Hub surface that
  // creates / updates / cancels a canonical task or appointment fires
  // `ghost:refresh` on `window` (see ScheduleAppointmentModal,
  // AppointmentDetailsModal, TasksPage, etc.).  We listen and refetch
  // the unified feed immediately — no server push required.
  useEffect(() => {
    const onGhostRefresh = () => { scheduleFetch('window_event'); };
    window.addEventListener('ghost:refresh', onGhostRefresh);
    // Refresh on tab focus too, so cross-session changes surface
    // without waiting for the 60s backstop.
    const onFocus = () => { scheduleFetch('focus'); };
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('ghost:refresh', onGhostRefresh);
      window.removeEventListener('focus', onFocus);
    };
  }, [scheduleFetch]);

  // Real-time updates via existing WS service
  useEffect(() => {
    const events = ['ghost:created', 'ghost:updated', 'ghost:completed',
                    'ghost:reassigned', 'ghost:dismissed'];
    const handler = () => { fetchItems(); };
    const unsubs = events.map((e) => wsService.subscribe?.(e, handler)).filter(Boolean);
    return () => { unsubs.forEach((u) => { try { u(); } catch (_e) { /* noop */ } }); };
  }, [fetchItems]);

  // Fallback refresh every 60s (safety net for automatic-rule pickup)
  useEffect(() => {
    const t = setInterval(fetchItems, 60000);
    return () => clearInterval(t);
  }, [fetchItems]);

  const onToggleExpand = (name) =>
    setSectionExpanded((s) => ({ ...s, [name]: !s[name] }));

  // v3.16-1238 — Completion flow now consults the reusable activity-completion
  // framework. If the item's activity type (or `require_completion_details`)
  // requires outcome+notes, we open the ActivityCompletionDialog instead of
  // completing directly. Otherwise we complete inline (unchanged behaviour).
  const [completionDialog, setCompletionDialog] = useState({
    open: false, item: null, rule: null,
  });

  // v3.18-1335 CALL-CMD-002 — Follow-Up Outcome Loop.  When the item
  // was created by a Call Outcome follow-up assignment (kind ===
  // "follow_up" and source === "call_outcome_assignment"), route
  // completion through the SAME Call Outcome modal the rep uses on
  // a live call, so the assignee logs an outcome + notes + optional
  // chained follow-up in one operation.  The modal calls the
  // /call-follow-ups/{id}/complete endpoint which closes the FU +
  // Ghost item, moves Last Activity, and preserves the ownership
  // chain in the Activity Timeline.
  const [followUpCompletion, setFollowUpCompletion] = useState(null);

  // v3.18-1297 — Inline detail panel state. Selecting an item opens
  // the slide-out panel WITHOUT navigating away.
  const [selectedItem, setSelectedItem] = useState(null);
  const flatItems = useMemo(() => [
    ...(sections.now?.visible || []), ...(sections.now?.overflow || []),
    ...(sections.today?.visible || []), ...(sections.today?.overflow || []),
    ...(sections.overdue?.visible || []), ...(sections.overdue?.overflow || []),
    ...(sections.upcoming?.visible || []), ...(sections.upcoming?.overflow || []),
  ], [sections]);
  const navigateInternal = useNavigate();
  const locationInternal = useLocation();
  const openClientFromPanel = (item) => {
    if (!item?.client_id) return;
    // v3.21-1620 · Ghost Daily Organizer detail panel now opens the
    // STANDARD Client Workspace instead of navigating to the classic
    // detail page.  This satisfies the "click client → work the
    // client from ONE dependable place" requirement.
    try {
      window.dispatchEvent(new CustomEvent('client-workspace:open', {
        detail: { clientId: item.client_id },
      }));
    } catch (_e) { /* noop */ }
  };
  const editItemNotes = async (item, notes) => {
    try {
      const { data } = await api.patch(`/ghost-items/${item.id}`, { description: notes });
      // Reflect locally + refresh.
      setSelectedItem((cur) => (cur?.id === item.id ? { ...cur, description: notes } : cur));
      fetchItems();
      return { ok: true, item: data };
    } catch (err) {
      return { ok: false, error: err?.response?.data?.detail || 'Save failed' };
    }
  };

  const finalizeCompletion = (itemId) => {
    setCompletingId(itemId);
    completingTimer.current = setTimeout(() => {
      setCompletingId(null);
      // v3.18-1299 Ghost Auto-Advance — when the completed item is
      // the one the operator is inspecting in the inline detail
      // panel, jump forward to the next task in the same filtered
      // queue instead of dropping them back into the list.
      setSelectedItem((cur) => {
        if (!cur || cur.id !== itemId) return cur;
        const idx = flatItems.findIndex((x) => x.id === itemId);
        // flatItems reflects the pre-refresh order — pick the item
        // AFTER the completed one, skipping the completed row.
        for (let i = idx + 1; i < flatItems.length; i += 1) {
          const cand = flatItems[i];
          if (cand && cand.id !== itemId) return cand;
        }
        for (let i = idx - 1; i >= 0; i -= 1) {
          const cand = flatItems[i];
          if (cand && cand.id !== itemId) return cand;
        }
        return null;
      });
      fetchItems();
    }, 2000);
  };

  const onComplete = async (item) => {
    if (completingId) return;

    // v3.18-1335 CALL-CMD-002 — Follow-up outcome loop.  Any Ghost
    // item that originated as a call-outcome follow-up assignment
    // MUST be closed through the Call Outcome modal so the assignee
    // logs a real outcome + notes and the chain audit is preserved.
    // The modal writes the outcome + closes the FU + Ghost item
    // in one shot via /call-follow-ups/{id}/complete.
    const isFollowUpItem = (item?.kind === 'follow_up')
      && (item?.source === 'call_outcome_assignment')
      && !!item?.call_follow_up_id;
    if (isFollowUpItem) {
      let clientPhone = '';
      try {
        if (item.client_id) {
          const { data } = await api.get(`/clients/${item.client_id}`);
          clientPhone = data?.phone || '';
        }
      } catch { /* phone is best-effort; modal still opens */ }
      setFollowUpCompletion({
        clientId:              item.client_id || null,
        clientName:            item.client_name || '',
        phone:                 clientPhone,
        direction:             'outbound',
        callLogId:             null,
        followUpId:            item.call_follow_up_id,
        ghostItemId:           item.id,
        followUpType:          item.title?.replace(/^Follow-up:\s*/, '') || '',
        followUpAssignedByName: item.created_by_name || '',
      });
      return;
    }

    let rule = null;
    try {
      const res = await api.get(`/ghost-items/${item.id}/completion-rule`);
      rule = res?.data?.rule || null;
    } catch (_e) {
      // Ignore — fall back to direct complete if the rule endpoint is down.
    }
    const needsForm = rule && (rule.requires_outcome || rule.requires_notes);

    if (needsForm) {
      setCompletionDialog({ open: true, item, rule });
      return;
    }

    // Fast-path: no outcome/notes required, complete directly.
    try {
      await api.post(`/ghost-items/${item.id}/complete`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Server may still require it (defence-in-depth). Open dialog if so.
      if (err?.response?.status === 422 && detail?.errors) {
        setCompletionDialog({ open: true, item, rule });
        return;
      }
      toast.error(typeof detail === 'string' ? detail : 'Failed to complete');
      return;
    }
    finalizeCompletion(item.id);
  };

  const submitCompletionForm = async (payload) => {
    const item = completionDialog.item;
    if (!item) return;
    await api.post(`/ghost-items/${item.id}/complete`, payload);
    toast.success('Item completed');
    finalizeCompletion(item.id);
  };
  useEffect(() => () => clearTimeout(completingTimer.current), []);

  // v3.18-1281 DEFECT-2 follow-up — Restore Ghost position on return from
  // client. GhostDailyOrganizer lives in the persistent layout (right rail)
  // and stays mounted across SPA route changes, so `loading`/`sections`
  // never change on back-nav. We depend on `location.pathname` so the
  // effect re-runs whenever the browser returns to /dashboard, and reset
  // scrollRestoredRef on each pathname change so subsequent round-trips
  // continue to work within a single mount lifetime.
  const scrollRestoredRef = useRef(false);
  useEffect(() => {
    // Reset the guard whenever the route changes so the next return
    // through openClient() can restore again.
    scrollRestoredRef.current = false;
  }, [location.pathname]);

  useEffect(() => {
    if (loading) return;
    if (scrollRestoredRef.current) return;
    try {
      const raw = sessionStorage.getItem('ghost-organizer-return-state');
      if (!raw) return;
      const snap = JSON.parse(raw);
      if (!snap.savedAt || (Date.now() - snap.savedAt) > 15 * 60 * 1000) {
        sessionStorage.removeItem('ghost-organizer-return-state');
        scrollRestoredRef.current = true;
        return;
      }
      if (snap.pathname && !location.pathname.startsWith(snap.pathname.split('?')[0])) {
        return;
      }
      scrollRestoredRef.current = true;
      const targetY = snap.scrollY || 0;
      // v3.18-1285 — Bounded-poll scroll restore. The old double-rAF
      // fired before layout height was committed (webpack + async
      // right-rail data), so window.scrollTo silently clamped to 0.
      // Poll every 60 ms for up to 3 s and stop as soon as the target
      // scrollY is achievable AND matches within ±5 px.
      const startedAt = Date.now();
      const attempt = () => {
        const maxScroll = Math.max(
          0,
          document.documentElement.scrollHeight - window.innerHeight,
        );
        const reachable = Math.min(targetY, maxScroll);
        window.scrollTo({ top: reachable, behavior: 'auto' });
        const reached = Math.abs(window.scrollY - reachable) <= 5;
        if (reached || (Date.now() - startedAt) > 3000) {
          sessionStorage.removeItem('ghost-organizer-return-state');
          return;
        }
        setTimeout(attempt, 60);
      };
      requestAnimationFrame(attempt);
    } catch { /* ignore */ }
  }, [loading, sections, location.pathname]);

  // Critical count → red badge when collapsed
  const criticalCount = useMemo(() => {
    const now = sections?.now || {};
    const items = [...(now.visible || []), ...(now.overflow || [])];
    return items.filter((i) => i.priority === 'critical').length;
  }, [sections]);

  const totalActive = (sections.now?.total || 0)
                      + (sections.today?.total || 0)
                      + (sections.overdue?.total || 0)
                      + (sections.upcoming?.total || 0);

  // v3.21-1823 · Persistent OVERDUE count exposed at the dock header so
  // the operator can never overlook missed / needs-attention items.
  const overdueCount = sections.overdue?.total || 0;

  // ── Collapsed rail ──────────────────────────────────────────────
  if (!effectiveExpanded) {
    return (
      <>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          data-testid="ghost-dock-collapsed"
          className="fixed right-0 top-1/2 -translate-y-1/2 z-50
                     h-24 w-14 bg-primary text-accent
                     border-l-2 border-y-2 border-accent/40
                     rounded-l-xl shadow-2xl
                     flex flex-col items-center justify-center gap-1.5
                     hover:bg-slate-800 hover:border-accent transition"
          title="Open Ghost Daily Organizer"
        >
          <img
            src={GHOST_TRADEMARK_SRC}
            alt="Ghost"
            className="h-8 w-8 object-contain"
            draggable={false}
          />
          {criticalCount > 0 ? (
            <span
              data-testid="ghost-dock-badge"
              className="text-[10px] font-bold bg-red-500 text-white rounded-full px-1.5 min-w-[20px] text-center"
            >
              {criticalCount}
            </span>
          ) : totalActive > 0 ? (
            <span className="text-[10px] font-bold text-accent/80">{totalActive}</span>
          ) : null}
        </button>
        {/* Dialog must be reachable even from the collapsed rail. */}
        <CreateGhostItemDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          currentUser={currentUser}
          onCreated={fetchItems}
        />
      </>
    );
  }

  // ── Expanded dock — mirrors the left sidebar architecture ─────────
  // v3.16-1237: full-height (inset-y-0), matched header size, matched
  // typography, approved Ghost trademark. NOT a widget — a permanent
  // operational console.
  return (
    <>
      <aside
        data-testid="ghost-dock-expanded"
        className="hidden xl:flex fixed inset-y-0 right-0 w-64 z-50
                   bg-primary border-l border-primary-light/20 shadow-2xl
                   flex-col overflow-hidden"
      >
        {/* Header — exact bookend match to the left sidebar logo block. */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-primary-light/20">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src={GHOST_TRADEMARK_SRC}
              alt="Ghost"
              className="h-11 w-11 object-contain shrink-0 drop-shadow"
              draggable={false}
              data-testid="ghost-dock-trademark"
            />
            <div className="min-w-0">
              <h2 className="font-heading text-xl font-bold text-white tracking-tight leading-tight">
                Ghost Daily Organizer
              </h2>
              <p className="text-xs text-white/60 leading-tight">
                Live Command Center
              </p>
            </div>
          </div>
        </div>

        {/* Action bar — mirrors the sidebar's admin-tools row spacing. */}
        <div className="px-4 pt-4 pb-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-xs text-white/60">
            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="font-semibold uppercase tracking-wider text-[11px]">Live</span>
            <span className="text-white/40">·</span>
            <span data-testid="ghost-active-count" className="font-semibold text-white/80">
              {totalActive} active
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 text-accent hover:text-gold-light hover:bg-accent/10 rounded-md"
              onClick={() => setCreateOpen(true)}
              data-testid="ghost-add-btn"
              title="Add item"
            >
              <Plus className="h-4 w-4" />
            </Button>
            {!narrow && (
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 text-white/60 hover:text-white hover:bg-white/10 rounded-md"
                onClick={() => setExpanded(false)}
                data-testid="ghost-collapse-btn"
                title="Collapse dock"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* v3.16-1239 — Mine / Team filter for managers+ */}
        {canSeeTeam && (
          <div className="px-4 pt-1 pb-2" data-testid="ghost-scope-filter">
            <div className="inline-flex rounded-md bg-white/5 p-0.5">
              <button
                type="button"
                onClick={() => setScope('me')}
                data-testid="ghost-scope-mine"
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded text-[11px] font-bold uppercase tracking-wider transition
                            ${scope === 'me'
                              ? 'bg-accent text-primary'
                              : 'text-white/60 hover:text-white'}`}
              >
                <User className="h-3 w-3" />
                Mine
              </button>
              <button
                type="button"
                onClick={() => setScope('team')}
                data-testid="ghost-scope-team"
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded text-[11px] font-bold uppercase tracking-wider transition
                            ${scope === 'team'
                              ? 'bg-accent text-primary'
                              : 'text-white/60 hover:text-white'}`}
              >
                <Users className="h-3 w-3" />
                Team
              </button>
            </div>
          </div>
        )}

        {/* Body — readable typography, only content scrolls. */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {loading ? (
            <div className="text-center text-sm text-white/60 py-8">Loading…</div>
          ) : (
            <>
              <GhostSection
                name="now"      section={sections.now}
                onComplete={onComplete} completingId={completingId}
                expanded={sectionExpanded.now}
                onToggleExpand={onToggleExpand}
                userId={currentUser?.id}
                currentUser={currentUser}
                onReassigned={fetchItems}
                showAssignee={scope === 'team'}
                onSelect={setSelectedItem}
                selectedId={selectedItem?.id}
              />
              <GhostSection
                name="today"    section={sections.today}
                onComplete={onComplete} completingId={completingId}
                expanded={sectionExpanded.today}
                onToggleExpand={onToggleExpand}
                userId={currentUser?.id}
                currentUser={currentUser}
                onReassigned={fetchItems}
                showAssignee={scope === 'team'}
                onSelect={setSelectedItem}
                selectedId={selectedItem?.id}
              />
              {/* v3.21-1823 · OVERDUE — items that must NEVER disappear. */}
              <GhostSection
                name="overdue"  section={sections.overdue}
                onComplete={onComplete} completingId={completingId}
                expanded={sectionExpanded.overdue}
                onToggleExpand={onToggleExpand}
                userId={currentUser?.id}
                currentUser={currentUser}
                onReassigned={fetchItems}
                showAssignee={scope === 'team'}
                onSelect={setSelectedItem}
                selectedId={selectedItem?.id}
              />
              <GhostSection
                name="upcoming" section={sections.upcoming}
                onComplete={onComplete} completingId={completingId}
                expanded={sectionExpanded.upcoming}
                onToggleExpand={onToggleExpand}
                userId={currentUser?.id}
                currentUser={currentUser}
                onReassigned={fetchItems}
                showAssignee={scope === 'team'}
                onSelect={setSelectedItem}
                selectedId={selectedItem?.id}
              />
            </>
          )}
        </div>

        {/* Footer badge — matches the version footer under the left nav. */}
        <div className="border-t border-primary-light/20 bg-primary px-4 py-3 flex items-center justify-between">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-white/50">
            Operational Console
          </span>
          <span className="text-[11px] text-accent font-semibold">GDO</span>
        </div>
      </aside>
      <CreateGhostItemDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        currentUser={currentUser}
        onCreated={fetchItems}
      />
      {/* v3.16-1238 — reusable outcome/notes dialog for Ghost completion.
          v3.18-1351 GHOST-CONTEXT — now carries full client identity so
          operators see WHO the task is for before completing it, plus a
          prominent "Open Client Record" button. */}
      <ActivityCompletionDialog
        open={completionDialog.open}
        onOpenChange={(v) => setCompletionDialog((s) => ({ ...s, open: v }))}
        rule={completionDialog.rule}
        onSubmit={submitCompletionForm}
        itemTitle={completionDialog.item?.title || 'Complete Ghost item'}
        itemSubtitle="Record the outcome and notes before closing this Ghost card."
        submitLabel="Complete Ghost Item"
        testIdPrefix="ghost-completion"
        contextClient={(() => {
          const it = completionDialog.item;
          if (!it || !it.client_id || !it.client_name) return null;
          const amt = (typeof it.client_deal_amount === 'number' && it.client_deal_amount > 0)
            ? it.client_deal_amount.toLocaleString('en-US',
                { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
            : null;
          const due = humanizeDue(it.due_at);
          return {
            id:              it.client_id,
            name:            it.client_name,
            company:         it.client_company,
            rep_name:        it.client_assigned_rep_name,
            phone:           it.client_phone,
            email:           it.client_email,
            stage:           it.client_stage,
            deal_amount:     amt,
            funding_company: it.client_funding_company,
            due_label:       due?.label || null,
            task_type:       it.title,
            onOpenClient:    () => {
              try {
                sessionStorage.setItem('ghost-organizer-return-state', JSON.stringify({
                  scrollY: window.scrollY || 0, savedAt: Date.now(),
                  pathname: location.pathname + (location.search || ''),
                }));
              } catch { /* best effort */ }
              setCompletionDialog((s) => ({ ...s, open: false }));
              navigate(`/clients/${it.client_id}`, {
                state: {
                  returnTo: location.pathname + (location.search || ''),
                  returnLabel: 'Ghost Daily Organizer',
                },
              });
            },
          };
        })()}
      />
      {/* v3.21-1612 · Compact follow-up completion dialog.  Replaces
          the retired CallOutcomeModal for the Ghost follow-up loop.
          Live calls use ActiveCallPill's Dispose + Notes + End Call. */}
      <FollowUpCompleteDialog
        open={!!followUpCompletion}
        data={followUpCompletion}
        onComplete={() => {
          const ghostId = followUpCompletion?.ghostItemId;
          setFollowUpCompletion(null);
          if (ghostId) finalizeCompletion(ghostId);
          fetchItems();
        }}
      />
      {/* v3.18-1297 — inline detail panel. Opens WITHOUT navigating away. */}
      {selectedItem && (
        <GhostTaskDetailPanel
          item={selectedItem}
          items={flatItems}
          onClose={() => setSelectedItem(null)}
          onNavigate={(next) => setSelectedItem(next)}
          onEditNotes={editItemNotes}
          onGoToClient={openClientFromPanel}
        />
      )}
    </>
  );
};

export default GhostDailyOrganizer;
